import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

/**
 * SidebarContext value type
 */
interface SidebarContextType {
  /** Whether the sidebar is collapsed */
  isCollapsed: boolean;
  /** Toggle the collapse state */
  toggleCollapse: () => void;
  /** Expand the sidebar */
  expandSidebar: () => void;
  /** Collapse the sidebar */
  collapseSidebar: () => void;
}

export const SIDEBAR_STATE_KEY = 'zentime_sidebar_collapsed';

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  toggleCollapse: () => {},
  expandSidebar: () => {},
  collapseSidebar: () => {},
});

/**
 * Provider component that makes sidebar state available throughout the app
 * @param children - React children components
 */
export function SidebarProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage if available, otherwise default to false
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
    return savedState ? JSON.parse(savedState) : false;
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  const expandSidebar = () => {
    // Always set it to false regardless of current state
    setIsCollapsed(false);
  };

  const collapseSidebar = () => {
    // Always set it to true regardless of current state
    setIsCollapsed(true);
  };

  return (
    <SidebarContext.Provider
      value={{ isCollapsed, toggleCollapse, expandSidebar, collapseSidebar }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

/**
 * Hook for accessing sidebar state throughout the app
 * @returns Object containing the current sidebar state and methods to control it
 */
export const useSidebar = () => useContext(SidebarContext);
